import { TableColumn } from "@/components/Table/Table";
import { TItem } from "@/components/SortableList/SortableList";

interface ApplyPersistedColumnConfigOptions {
	/**
	 * Column uids whose visibility is view-driven and must NOT be overridden by the persisted config
	 * (e.g. the List "Exclusion reason" column, shown only on the Unsure / Not found views). Their
	 * `show` value from the freshly-built defaults is preserved. PREC-3567.
	 */
	viewDrivenVisibilityUids?: string[];
}

/**
 * Applies a user's persisted column config (order + visibility) on top of the freshly-built default
 * columns. Shared by the List detail leads table and the Contacts table so both merge saved configs
 * the same way.
 */
export const applyPersistedColumnConfig = (
	defaultColumns: TableColumn[],
	tableConfig: TItem[],
	options: ApplyPersistedColumnConfigOptions = {}
): TableColumn[] => {
	if (!tableConfig?.length) {
		return defaultColumns;
	}

	const viewDrivenVisibility = new Set(options.viewDrivenVisibilityUids ?? []);

	let columns = [...defaultColumns];

	for (const c of columns) {
		const configIndex = tableConfig.findIndex((tc) => tc?.uid === c.uid);
		const currentIndex = columns.findIndex((tc) => tc?.uid === c.uid);

		if (configIndex > -1 && currentIndex > -1 && configIndex !== currentIndex) {
			const newConfigColumn = columns[currentIndex];

			columns.splice(currentIndex, 1);

			columns = [...columns.slice(0, configIndex), newConfigColumn, ...columns.slice(configIndex)];
		}
	}

	return columns.map((c) => {
		if (c?.uid && viewDrivenVisibility.has(c.uid)) {
			return c;
		}

		const config = tableConfig.find((tc) => tc?.uid === c?.uid);

		if (config) {
			c.show = config.show;
			c.locked = config.locked;
		}

		return c;
	});
};
